import { useTranslation } from 'react-i18next';
import { ZoomInIcon, ZoomOutIcon } from '@/utils/icons';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import strip from '../ControlsRow.module.css';
import styles from './ZoomControls.module.css';

interface IZoomControlsProps {
  zoom: number | null; // Percent of original size (100 = original); null while the content is measured.
  onZoomChange: (pct: number) => void;
  onZoomDelta: (direction: 1 | -1) => void;
}

/** The zoom cluster: stepped − / + buttons around the percent input. */
export function ZoomControls({ zoom, onZoomChange, onZoomDelta }: IZoomControlsProps) {
  const { t } = useTranslation();

  return (
    <div className={strip.group}>
      <Tooltip content={`${t('documents.markup.zoomOut')} (−)`}>
        <button
          type="button"
          className={strip.btn}
          onClick={() => onZoomDelta(-1)}
          disabled={zoom === null || zoom <= 1}
          aria-label={t('documents.markup.zoomOut')}
          aria-keyshortcuts="-"
        >
          <ZoomOutIcon size={15} />
        </button>
      </Tooltip>

      <span className={styles.field}>
        <Tooltip content={t('documents.markup.zoom')}>
          <input
            type="number"
            className={styles.input}
            value={zoom ?? ''}
            min={1}
            max={200}
            onChange={(e) => {
              const pct = Math.round(Number(e.target.value));
              if (Number.isFinite(pct)) onZoomChange(pct);
            }}
            disabled={zoom === null}
            aria-label={t('documents.markup.zoom')}
          />
        </Tooltip>
        <span className={styles.suffix} aria-hidden="true">%</span>
      </span>

      <Tooltip content={`${t('documents.markup.zoomIn')} (+)`}>
        <button
          type="button"
          className={strip.btn}
          onClick={() => onZoomDelta(1)}
          disabled={zoom === null || zoom >= 200}
          aria-label={t('documents.markup.zoomIn')}
          aria-keyshortcuts="+"
        >
          <ZoomInIcon size={15} />
        </button>
      </Tooltip>
    </div>
  );
}